import Image from '@global/Image'
import IconArrowUpRight from '@icons/ArrowUpRight'
import IconCarouselArrowLeft from '@icons/CarouselArrowLeft'
import IconCarouselArrowRight from '@icons/CarouselArrowRight'
import IconClose from '@icons/Close'
import IconDownload from '@icons/Download'
import IconMinus from '@icons/Minus'
import IconPlay from '@icons/Play'
import IconPlus from '@icons/Plus'
import { formatDate } from '@lib/date'
import { getYoutubeThumbnail } from '@lib/helpers'
import { defaultImages } from '@lib/variables'
import useWindowSize from '@hooks/useWindowSize'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import * as S from './GalleryMain.styled'

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })

const NextArrow = ({ onClick, className }) => {
  return (
    <S.CarouselNext onClick={onClick} className={className}>
      <IconCarouselArrowRight />
    </S.CarouselNext>
  )
}

const PrevArrow = ({ onClick, className }) => {
  return (
    <S.CarouselPev onClick={onClick} className={className}>
      <IconCarouselArrowLeft />
    </S.CarouselPev>
  )
}

const GalleryMain = ({
  items = [],
  initialSlide,
  onIndexChange,
  fullwidth,
  onImageClick,
  isVideoButtonClick,
  address,
  created,
  isInModal,
  isFullscreen,
  setIsFullscreen,
}) => {
  const [isVideoPlaying, setVideoPlaying] = useState(false)
  const [zoomStates, setZoomStates] = useState({})
  const slider = useRef(null)
  const transformRefs = useRef({})
  const windowSize = useWindowSize()
  const isMobile = windowSize?.width && windowSize.width <= 767

  const settings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    lazyLoad: true,
    focusOnSelect: false,
    draggable: isInModal && isMobile,
    swipe: isMobile,
    touchMove: isMobile,
    touchThreshold: 8,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (_, newIndex) => {
      if (!isVideoButtonClick) {
        setVideoPlaying(false)
      }
      onIndexChange && onIndexChange(newIndex)
    },
  }

  useEffect(() => {
    slider.current.slickGoTo(initialSlide)
  }, [initialSlide])

  useEffect(() => {
    if (isVideoButtonClick) {
      setVideoPlaying(true)
    }
  }, [isVideoButtonClick])

  const getImageAltText = (index) => {
    return `${address || ''} - Image ${index}${created ? ` - ${formatDate(created, 'dd/MM/yyyy')}` : ''
      }`
  }

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen)

  const handleZoomIn = () => {
    const currentSlide = slider.current?.innerSlider?.state?.currentSlide || 0
    const transformRef = transformRefs.current[currentSlide]
    if (transformRef) {
      const currentScale = getCurrentSlideZoom()
      if (currentScale < 1) {
        transformRef.resetTransform()
      } else {
        transformRef.zoomIn()
      }
    }
  }

  const handleZoomOut = () => {
    const currentSlide = slider.current?.innerSlider?.state?.currentSlide || 0
    const transformRef = transformRefs.current[currentSlide]
    if (transformRef) {
      const currentScale = getCurrentSlideZoom()
      if (currentScale > 1) {
        transformRef.resetTransform()
      } else {
        transformRef.zoomOut()
      }
    }
  }

  const handleZoomStateChange = (slideIndex, scale) => {
    setZoomStates((prev) => ({
      ...prev,
      [slideIndex]: scale,
    }))
  }

  const getCurrentSlideZoom = () => {
    const currentSlide = slider.current?.innerSlider?.state?.currentSlide || 0
    return zoomStates[currentSlide] || 1
  }

  const handleDownload = async () => {
    const currentSlide = slider.current?.innerSlider?.state?.currentSlide || 0
    const currentItem = items[currentSlide]

    // Only allow download for images and floorplans
    if (
      !currentItem ||
      (currentItem.link_type !== 'image' &&
        currentItem.link_type !== 'floorplans')
    ) {
      return
    }

    try {
      const imageUrl = currentItem.href

      const response = await fetch(imageUrl)
      const blob = await response.blob()

      const fileExtension = imageUrl.split('.').pop().split('?')[0] || 'jpg'
      const filename = `${address || 'property'}-image-${currentSlide + 1}.${fileExtension}`

      // iOS: Open Share Sheet -> Save Image
      const file = new File([blob], filename, {
        type: blob.type || 'image/jpeg',
      })

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

      if (
        isIOS &&
        navigator.share &&
        navigator.canShare
      ) {
        await navigator.share({
          files: [file],
          title: filename,
        })
      } else {
        // Android/Desktop fallback
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = filename

        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        URL.revokeObjectURL(url)
      }
    } catch {
      window.open(currentItem.href, '_blank')
    }
  }

  const canDownloadCurrentSlide = () => {
    const currentSlide = slider.current?.innerSlider?.state?.currentSlide || 0
    const currentItem = items[currentSlide]
    return (
      currentItem &&
      (currentItem.link_type === 'image' ||
        currentItem.link_type === 'floorplans')
    )
  }

  const renderImage = (it, key, linkType) => {
    const scale = zoomStates[key] ?? 1
    const isZoomed = scale > 1

    return isInModal ? (
      <TransformWrapper
        ref={(ref) => {
          if (ref) {
            transformRefs.current[key] = ref
          }
        }}
        initialScale={1}
        minScale={1}
        maxScale={3}
        wheel={{
          step: 0.1,
        }}
        panning={{
          disabled: !isZoomed,
        }}
        doubleClick={{
          disabled: false,
          mode: 'zoomIn',
        }}
        onTransformed={(_, state) => {
          handleZoomStateChange(key, state.scale)
        }}
      >
        <TransformComponent
          wrapperStyle={{
            touchAction: isZoomed ? undefined : 'pan-y pinch-zoom',
          }}
        >
          <div onClick={() => onImageClick && onImageClick(key)} className={linkType === 'floorplans' ? 'floorplan' : ''}>
            <Image
              src={it.href}
              fallbackImage={defaultImages.mediumNavy}
              quickload
              alt={getImageAltText(key + 1)}
            />
          </div>
        </TransformComponent>
      </TransformWrapper>
    ) : (
      <Image
        src={it.href}
        fallbackImage={defaultImages.mediumNavy}
        quickload
        alt={getImageAltText(key + 1)}
        onClick={() => onImageClick && onImageClick(key)}
      />
    )
  }

  return (
    <>
      <Head>
        {items.length > 0 && (
          <link rel='preload' href={items[0].href} as='image' />
        )}
      </Head>
      {isFullscreen && <S.HideNav />}
      {isInModal && <S.MainStyle />}
      <S.GalleryMain fullwidth={fullwidth} fullscreen={isFullscreen}>
        <Slider ref={slider} {...settings}>
          {items.map((it, key) => (
            <S.GalleryItem key={`gallery-${key}`}>
              {it.link_type === 'image' || it.link_type === 'floorplans' ? (
                renderImage(it, key, it.link_type)
              ) : it.link_type === 'video' ? (
                <ReactPlayer
                  playing={isVideoPlaying}
                  light={
                    it?.href?.href
                      ? getYoutubeThumbnail(it.href.href)
                      : defaultImages.mediumNavy
                  }
                  url={it.href.href}
                  width='100%'
                  height='100%'
                  playIcon={<IconPlay onClick={() => setVideoPlaying(true)} />}
                />
              ) : it.link_type === 'tour' ? (
                <iframe src={it.href} width='100%' height='100%' />
              ) : null}
            </S.GalleryItem>
          ))}
        </Slider>
        {isInModal && (
          <>
            {isFullscreen && (
              <S.CloseFullscreenButton onClick={toggleFullscreen} title="Close gallery">
                <IconClose />
              </S.CloseFullscreenButton>
            )}
            <S.GalleryActions>
              <button
                disabled={getCurrentSlideZoom() === 3}
                onClick={handleZoomIn}
              >
                <IconPlus />
              </button>
              <button
                onClick={handleZoomOut}
                disabled={getCurrentSlideZoom() <= 1}
              >
                <IconMinus />
              </button>
              {!isFullscreen && (
                <S.FullscreenButtonWrapper onClick={toggleFullscreen} title="View Fullscreen">
                  <S.FullscreenButton>
                    <IconArrowUpRight />
                  </S.FullscreenButton>
                </S.FullscreenButtonWrapper>
              )}
              {canDownloadCurrentSlide() && (
                <S.DownloadButtonWrapper onClick={handleDownload} title="Download image">
                  <S.DownloadButton>
                    <IconDownload />
                  </S.DownloadButton>
                </S.DownloadButtonWrapper>
              )}
            </S.GalleryActions>
          </>
        )}
      </S.GalleryMain>
    </>
  )
}

export default GalleryMain
